<script lang="ts">
  /**
   * A trip, as an object page — Pattern 2 in `03-shell-and-layout.md`, and the
   * home of F13.
   *
   * The page is built around one claim: **a trip knows what kind of trip it is**,
   * and everything else follows from that. The questions, the cost lines and the
   * pack list are all the kind's, not a generic set with irrelevant rows greyed
   * out. A self-catering bush booking asks about load-shedding, water and whether
   * the 4x4 is serviced; a hotel is never asked any of it, because it was never
   * offered the question.
   *
   * The layout obeys the pattern exactly, and the two rules that are easy to lose:
   *
   *  · **One number in the hero.** Total cost. Everything else — distance, drive
   *    time, fuel, tolls, beds, cost per night — is in the facts spine.
   *  · **History reads as sentences**, newest first, with machine entries in the
   *    same stream marked by an amber dot rather than moved to their own tab.
   *
   * Reads go through the offline cache so the sheet opens in a car with no signal.
   * Writes do not, and the page says so plainly rather than accepting an edit it
   * cannot keep.
   */
  import { Icon, Panel, zar, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { loadCached, net } from "../lib/offline.svelte";
  import { driveTime, type TripDetail, type TripKind } from "../lib/trips";
  import PackList from "./PackList.svelte";
  import AwayProfile from "./AwayProfile.svelte";

  let { tripId, onback }: { tripId: string; onback: () => void } = $props();

  let detail = $state<TripDetail | null>(null);
  let kinds = $state<TripKind[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let cachedAt = $state<string | null>(null);
  let saving = $state(false);
  let saveErr = $state<string | null>(null);
  let showCosts = $state(false);

  async function load() {
    loading = true;
    const r = await loadCached<TripDetail>(`/trips/${tripId}`);
    detail = r.data;
    error = r.error;
    cachedAt = r.fresh ? null : r.at;
    loading = false;
    if (r.data?.costs) {
      draft = {
        fuelPricePerL: str(r.data.costs.fuelPricePerL),
        consumptionLPer100km: str(r.data.costs.consumptionLPer100km),
        lines: Object.fromEntries(r.data.costs.lines.filter((l) => !l.derived).map((l) => [l.id, str(l.amount)])),
      };
    }
  }
  const str = (n: number | null) => (n == null ? "" : String(n));

  $effect(() => {
    void load();
  });

  // The catalogue is cached too: without it the "what kind of trip is this?"
  // choice — the one thing this page exists to ask — would be unavailable in
  // exactly the moment somebody is sitting in a car deciding.
  $effect(() => {
    void loadCached<{ kinds: TripKind[] }>("/trip-kinds").then((r) => (kinds = r.data?.kinds ?? []));
  });

  type Draft = { fuelPricePerL: string; consumptionLPer100km: string; lines: Record<string, string> };
  let draft = $state<Draft>({ fuelPricePerL: "", consumptionLPer100km: "", lines: {} });

  async function patch(body: Record<string, unknown>) {
    saving = true;
    saveErr = null;
    try {
      await api.patch(`/trips/${tripId}`, body);
      await load();
    } catch (e) {
      saveErr =
        e instanceof ApiError
          ? e.message
          : "Could not save that. Editing needs signal — the sheet and the pack list do not.";
    } finally {
      saving = false;
    }
  }

  const setKind = (kind: string) => patch({ kind });
  const answer = (id: string, value: "yes" | "no") => {
    const answers: Record<string, string> = {};
    for (const q of detail?.questions ?? []) if (q.answer) answers[q.id] = q.answer;
    // Clicking the answer you already gave clears it. There is no third button,
    // and "I no longer know" is a real state for a question about a booking.
    if (answers[id] === value) delete answers[id];
    else answers[id] = value;
    return patch({ answers });
  };

  function saveCosts() {
    const n = (s: string) => (s.trim() === "" ? null : Number(s));
    const lines: Record<string, number | null> = {};
    for (const [k, v] of Object.entries(draft.lines)) lines[k] = n(v);
    return patch({
      costs: {
        fuelPricePerL: n(draft.fuelPricePerL),
        consumptionLPer100km: n(draft.consumptionLPer100km),
        lines,
      },
    });
  }

  const trip = $derived(detail?.trip ?? null);
  const kind = $derived(detail?.kind ?? null);

  const daysAway = $derived.by(() => {
    if (!trip?.from || trip.status !== "planned") return null;
    return Math.round((Date.parse(trip.from) - Date.now()) / 86_400_000);
  });

  /** Glyph AND word AND colour, in that order of importance. Never colour alone. */
  const chip = $derived.by(() => {
    if (!trip) return { glyph: "pause", word: "Loading", tone: "idle" };
    if (trip.status === "done") return { glyph: "check", word: "Been there", tone: "ok" };
    if (trip.status === "idea") return { glyph: "pause", word: "An idea, no dates", tone: "idle" };
    const d = daysAway;
    if (d != null && d >= 0 && d <= 30) return { glyph: "alert", word: d === 0 ? "Today" : `In ${d} days`, tone: "warn" };
    return { glyph: "calendar", word: "Planned", tone: "ok" };
  });

  /** The one number. Everything else belongs in the spine. */
  const hero = $derived.by(() => {
    if (!trip) return { value: "—", label: "" };
    if (trip.status === "done" && trip.totalCost != null) {
      return { value: zar(trip.totalCost), label: "actually spent" };
    }
    if (trip.plannedTotal != null) {
      const blanks = trip.costBlanks ?? 0;
      return {
        value: zar(trip.plannedTotal),
        label: blanks === 0 ? "all in, every line costed" : `so far — ${blanks} ${blanks === 1 ? "line" : "lines"} still blank`,
      };
    }
    return { value: "—", label: "nothing costed yet" };
  });

  const provenance = $derived.by(() => {
    if (!trip) return "";
    const bits: string[] = [];
    if (trip.from) bits.push(trip.to ? `${dateZA(trip.from)} → ${dateZA(trip.to)}` : dateZA(trip.from));
    else bits.push("No dates yet");
    if (trip.nights != null) bits.push(`${trip.nights} ${trip.nights === 1 ? "night" : "nights"}`);
    bits.push(trip.kindLabel ? trip.kindLabel.toLowerCase() : "kind of trip not set");
    return bits.join(" · ");
  });

  const line = (id: string) => detail?.costs?.lines.find((l) => l.id === id) ?? null;

  /**
   * The facts spine. Right-set, one fact per row, em dash wherever the source is
   * not wired — never a plausible figure. The `why` is what turns a dash from a
   * bug into an answer.
   */
  const facts = $derived.by(() => {
    const c = detail?.costs ?? null;
    const beds = line("beds");
    const tolls = line("tolls");
    const fuel = line("fuel");
    return [
      {
        k: "Distance from Pretoria",
        v: trip?.driveKm != null ? `${trip.driveKm} km` : "—",
        why: trip?.driveKm == null ? "Not recorded. Type it in the trip book." : "One way, from Faerie Glen.",
      },
      {
        k: "Drive time",
        v: driveTime(trip?.driveMinutes ?? null),
        why: trip?.driveMinutes == null ? "Not recorded — and not estimated from the distance." : "One way, without stops.",
      },
      {
        k: "Fuel",
        v: fuel ? zar(fuel.amount) : "—",
        why:
          fuel?.amount != null && c?.fuelPricePerL != null
            ? `There and back at R ${c.fuelPricePerL.toFixed(2)}/ℓ, the price stored with this trip.`
            : (fuel?.blankBecause ?? "This kind of trip has no fuel line."),
      },
      {
        k: "Tolls",
        v: tolls ? zar(tolls.amount) : "—",
        why: tolls ? (tolls.blankBecause ?? "Both ways.") : "Not a line on this kind of trip.",
      },
      {
        k: "Accommodation",
        v: beds ? zar(beds.amount) : "—",
        why: beds ? (beds.blankBecause ?? "The whole booking.") : "Not a line on this kind of trip.",
      },
      {
        k: "Cost per night",
        v: c?.perNight != null ? zar(c.perNight) : "—",
        why:
          kind && !kind.nights
            ? "A day out has no nights."
            : c?.perNight != null
              ? "Beds and food only. Fuel and tolls do not change with the nights."
              : "Needs the dates and at least one per-night line.",
      },
    ];
  });

  /**
   * Cross-portal references.
   *
   * Three of these are real links in the design and none of the three is wired:
   * Waypoint has no service identity with Vault, Homestead or HQ yet. They are
   * listed with an em dash and the reason, rather than hidden — a page that
   * silently omits the booking confirmation reads as *there isn't one*.
   *
   * The house-sitter's pass used to be a fourth em dash here. It now has a real
   * path and its own section below, so this row points at that rather than
   * repeating a state in two places at two different ages — which is how a page
   * ends up saying "not built" above and "armed" underneath.
   */
  const linked = [
    { portal: "Vault", what: "The booking confirmation", why: "Waypoint cannot ask Vault for a document yet." },
    { portal: "Homestead", what: "Jobs this trip raised", why: "No jobs are posted from a trip yet." },
    { portal: "HQ Finance", what: "The planned expense", why: "Sending a budget to HQ is not wired." },
    {
      portal: "Fabric",
      what: "The house-sitter's pass",
      why: "Stated in full under While you are away, below — including what did not happen.",
    },
  ];
</script>

<div class="wrap">
  <nav class="crumb">
    <button class="lnk" onclick={onback}><Icon name="chevron-right" size={12} /> Trips</button>
    <span class="crumb__sep">/</span>
    <span class="crumb__here">{trip?.name ?? "Trip"}</span>
  </nav>

  {#if cachedAt}
    <p class="banner banner--quiet">
      <Icon name="pause" size={13} />
      <span
        ><strong>No signal.</strong> This is the sheet as it was on {dateZA(cachedAt)}, saved on this device. The pack list still
        ticks; nothing else can be changed until there is signal.</span
      >
    </p>
  {/if}

  <Panel {loading} {error} isEmpty={!loading && !error && !trip} onretry={load} variant="card" rows={2}>
    {#snippet empty()}
      That trip is not in the book any more. Go back to Trips and open one that is, or add it again — one field is
      all it takes.
    {/snippet}

    {#if trip}
      <div class="page">
        <!-- ── hero + facts ──────────────────────────────────────────────── -->
        <section class="hero card--hero">
          <div class="hero__ground" aria-hidden="true">
            <span class="hero__slot">Photograph — none yet</span>
          </div>
          <div class="hero__body">
            <span class="chip chip--{chip.tone}">
              <Icon name={chip.glyph} size={13} />
              {chip.word}
            </span>
            <h1 class="hero__name">{trip.name}</h1>
            <p class="hero__prov">{provenance}</p>
            {#if trip.blurb}<p class="hero__blurb">{trip.blurb}</p>{/if}
          </div>
          <div class="hero__num">
            <div class="fig">{hero.value}</div>
            <div class="fig__u">{hero.label}</div>
          </div>
        </section>

        <aside class="spine">
          <div class="card">
            <div class="kicker">Facts</div>
            {#each facts as f (f.k)}
              <div class="fact">
                <span class="fact__k">{f.k}</span>
                <span class="fact__v num">{f.v}</span>
                <span class="fact__w">{f.why}</span>
              </div>
            {/each}
          </div>

          <div class="card">
            <div class="kicker">Linked</div>
            {#each linked as l (l.portal)}
              <div class="fact">
                <span class="fact__k">{l.portal}</span>
                <span class="fact__v">—</span>
                <span class="fact__w">{l.what}. {l.why}</span>
              </div>
            {/each}
          </div>
        </aside>

        <!-- ── what kind of trip this is: F13's whole hinge ───────────────── -->
        <section class="card span">
          <div class="head">
            <span class="kicker"><Icon name="trip" size={13} /> What kind of trip</span>
            {#if kind}<span class="sub">{kind.sentence}</span>{/if}
          </div>
          <div class="kinds">
            {#each kinds as k (k.id)}
              <button
                class="kindbtn"
                class:kindbtn--on={k.id === trip.kind}
                onclick={() => setKind(k.id)}
                disabled={saving || !net.online}
                aria-pressed={k.id === trip.kind}
              >
                <span class="kindbtn__t">
                  {#if k.id === trip.kind}<Icon name="check" size={12} />{/if}
                  {k.label}
                </span>
                <span class="kindbtn__s">{k.sentence}</span>
              </button>
            {/each}
          </div>
          {#if !trip.kind}
            <p class="banner">
              <Icon name="alert" size={13} />
              <span
                ><strong>Not set.</strong> Until this says what sort of trip it is, there are no questions to answer, no
                cost lines and no pack list. Pick one above.</span
              >
            </p>
          {/if}
          {#if !net.online}
            <p class="sub">Choosing a kind needs signal. Everything already on this page can be read without it.</p>
          {/if}
          {#if saveErr}
            <p class="err"><Icon name="alert" size={12} /> {saveErr}</p>
          {/if}
        </section>

        <!-- ── the questions this kind asks ───────────────────────────────── -->
        <section class="card span">
          <div class="head">
            <span class="kicker"><Icon name="chat" size={13} /> The questions</span>
            <span class="sub">
              {#if !kind}Nothing to ask yet{:else if trip.unanswered}{trip.unanswered} still unanswered{:else}All answered{/if}
            </span>
          </div>
          <Panel loading={false} error={null} isEmpty={(detail?.questions.length ?? 0) === 0}>
            {#snippet empty()}
              These come with the kind of trip. Choose one above — a bush weekend asks about power, water and the 4x4;
              a day out asks whether the gate is even open.
            {/snippet}
            {#each detail?.questions ?? [] as q (q.id)}
              <div class="q" class:q--warn={q.answer === "no"}>
                <div class="grow">
                  <div class="q__ask">{q.ask}</div>
                  {#if q.answer === "no"}
                    <div class="q__no"><Icon name="alert" size={12} /> <span>{q.no}</span></div>
                  {:else if q.answer === "yes"}
                    <div class="q__yes"><Icon name="check" size={12} /> <span>Sorted</span></div>
                  {:else}
                    <div class="q__idle"><Icon name="pause" size={12} /> <span>Not answered</span></div>
                  {/if}
                </div>
                <div class="q__acts">
                  <button class="ans" class:ans--on={q.answer === "yes"} disabled={saving || !net.online} onclick={() => answer(q.id, "yes")}>
                    Yes
                  </button>
                  <button class="ans" class:ans--on={q.answer === "no"} disabled={saving || !net.online} onclick={() => answer(q.id, "no")}>
                    No
                  </button>
                </div>
              </div>
            {/each}
          </Panel>
        </section>

        <!-- ── what it costs, on this kind's own model ────────────────────── -->
        <section class="card span">
          <div class="head">
            <span class="kicker"><Icon name="fuel" size={13} /> What it costs</span>
            <button class="lnk" onclick={() => (showCosts = !showCosts)} disabled={!kind}>
              {showCosts ? "Close" : "Cost it"}
            </button>
          </div>
          <Panel loading={false} error={null} isEmpty={(detail?.costs?.lines.length ?? 0) === 0}>
            {#snippet empty()}
              A cost model belongs to a kind of trip. Choose one above and the lines appear — gate fees and firewood for
              the bush, parking and eating out for the coast.
            {/snippet}

            {#each detail?.costs?.lines ?? [] as l (l.id)}
              <div class="cost">
                <span class="cost__k">{l.label}</span>
                <span class="cost__v num">{zar(l.amount)}</span>
                <span class="cost__w">{l.blankBecause ?? l.hint}</span>
              </div>
            {/each}

            {#if showCosts && kind}
              <div class="editor">
                <div class="grid">
                  <label class="f">
                    <span>Petrol, R per litre</span>
                    <input type="number" step="0.01" bind:value={draft.fuelPricePerL} />
                  </label>
                  <label class="f">
                    <span>The car, litres per 100 km</span>
                    <input type="number" step="0.1" bind:value={draft.consumptionLPer100km} />
                  </label>
                  {#each kind.costs.filter((c) => !c.derived) as c (c.id)}
                    <label class="f">
                      <span>{c.label}, R</span>
                      <input type="number" step="0.01" bind:value={draft.lines[c.id]} />
                    </label>
                  {/each}
                </div>
                <p class="sub">
                  The petrol price is stored <em>with this trip</em>, not read from today's. A weekend costed in March
                  must still read March's price in August, or comparing it to what was actually spent means nothing.
                </p>
                <div class="acts">
                  <button class="btn-primary" onclick={saveCosts} disabled={saving || !net.online}>
                    {saving ? "Saving…" : "Save the costing"}
                  </button>
                </div>
              </div>
            {/if}
          </Panel>
        </section>

        <!-- ── F14 ───────────────────────────────────────────────────────── -->
        <div class="span"><PackList {tripId} /></div>

        <!-- ── F16 · what the house does while nobody is here ─────────────── -->
        <div class="span"><AwayProfile {tripId} onchanged={load} /></div>

        <!-- ── history, as sentences ──────────────────────────────────────── -->
        <section class="card span">
          <div class="head">
            <span class="kicker"><Icon name="clock" size={13} /> What has happened</span>
          </div>
          <Panel loading={false} error={null} isEmpty={(detail?.history.length ?? 0) === 0}>
            {#snippet empty()}
              Nothing recorded against this trip yet. Set the kind, answer a question or cost a line and it starts
              writing itself.
            {/snippet}
            {#each detail?.history ?? [] as h, i (i)}
              <p class="hist">
                <span class="hist__dot" class:hist__dot--machine={h.machine} aria-hidden="true"></span>
                <span>
                  <strong>{h.actor}</strong> {h.text}{#if h.machine}<span class="hist__mark"> · automatically</span>{/if} — {dateZA(h.at)}.
                </span>
              </p>
            {/each}
          </Panel>
        </section>

        <div class="span foot">
          <button class="lnk" onclick={() => window.print()}><Icon name="printer" size={13} /> Print the sheet</button>
          <span class="sub">One page, black on white. Readable in a car with no signal.</span>
        </div>
      </div>
    {/if}
  </Panel>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: var(--gap-section); }

  .crumb { display: flex; align-items: center; gap: 8px; font-size: var(--fs-micro); color: var(--mut); }
  .crumb__sep { opacity: 0.6; }
  .crumb__here { font-weight: 700; color: var(--tx2); }

  .page {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: var(--sp-4);
    align-items: start;
  }
  .span { grid-column: 1 / -1; }

  .card {
    padding: var(--sp-4) var(--pad-x) var(--sp-5);
    border: 1px solid var(--line);
    border-radius: var(--r-surface);
    background: var(--s1);
  }
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-3);
    margin-bottom: var(--sp-3);
  }
  .kicker { display: inline-flex; align-items: center; gap: 6px; }
  .sub { font-size: var(--fs-small); color: var(--mut); }

  /* ── hero ─────────────────────────────────────────────────────────────── */
  .hero { padding: 0 0 var(--sp-4); overflow: hidden; }
  /* A neutral ground with a labelled slot, never a stock image: the pattern is
     explicit that photography is the one place imagery appears, and a borrowed
     photograph of somebody else's lodge is worse than an honest gap. */
  .hero__ground {
    height: 120px;
    display: grid;
    place-items: center;
    background: var(--s2);
    border-bottom: 1px solid var(--line);
  }
  .hero__slot { font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .hero__body { padding: var(--sp-4) var(--pad-x) 0; }
  .hero__name {
    margin: var(--sp-2) 0 4px;
    font-size: var(--fs-h1);
    font-weight: 700;
    letter-spacing: -0.4px;
    color: var(--tx);
  }
  .hero__prov { margin: 0; font-size: var(--fs-small); color: var(--mut); }
  .hero__blurb { margin: var(--sp-2) 0 0; font-size: var(--fs-body); color: var(--tx2); }
  .hero__num { padding: var(--sp-4) var(--pad-x) 0; text-align: right; }
  .hero__num .fig__u { display: block; margin-top: 4px; }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: var(--r-pill);
    border: 1px solid var(--line);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .chip--ok { color: var(--ok); }
  .chip--warn { color: var(--warn); border-color: var(--warn); }
  .chip--idle { color: var(--mut); }

  /* ── facts spine ──────────────────────────────────────────────────────── */
  .spine { display: flex; flex-direction: column; gap: var(--sp-4); }
  .fact {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 2px var(--sp-3);
    min-height: var(--row-h);
    align-content: center;
    padding: var(--sp-2) 0;
    border-bottom: 1px solid var(--line);
  }
  .fact:last-child { border-bottom: none; }
  .fact__k { font-size: var(--fs-small); font-weight: 700; color: var(--tx2); }
  .fact__v {
    font-family: var(--font-figure);
    font-size: var(--fs-lead);
    font-weight: 700;
    color: var(--tx);
    text-align: right;
  }
  .fact__w { grid-column: 1 / -1; font-size: var(--fs-micro); color: var(--mut); }

  /* ── kinds ────────────────────────────────────────────────────────────── */
  .kinds { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: var(--sp-2); }
  .kindbtn {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-height: var(--row-h);
    padding: var(--sp-3);
    text-align: left;
    border: 1px solid var(--line);
    border-radius: var(--r-control);
    color: var(--tx2);
  }
  .kindbtn:hover { background: var(--card-hover); }
  /* Copper rule AND a tick AND the fill — three signals, so the selection
     survives any colour vision. */
  .kindbtn--on { box-shadow: inset 2px 0 0 var(--acc); background: var(--wash); color: var(--tx); }
  .kindbtn__t { display: inline-flex; align-items: center; gap: 5px; font-size: var(--fs-body); font-weight: 700; }
  .kindbtn__s { font-size: var(--fs-micro); color: var(--mut); }

  /* ── questions ────────────────────────────────────────────────────────── */
  .q {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    min-height: var(--row-h);
    padding: var(--sp-2) var(--sp-3);
    border-bottom: 1px solid var(--line);
  }
  .q--warn { border-left: 2px solid var(--warn); }
  .grow { flex: 1; min-width: 0; }
  .q__ask { font-size: var(--fs-body); font-weight: 600; color: var(--tx); }
  .q__no, .q__yes, .q__idle {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin-top: 3px;
    font-size: var(--fs-micro);
    font-weight: 700;
  }
  .q__no { color: var(--warn); }
  .q__yes { color: var(--ok); }
  .q__idle { color: var(--mut); }
  .q__acts { display: flex; gap: 6px; }
  .ans {
    padding: 5px 12px;
    border: 1px solid var(--line);
    border-radius: var(--r-pill);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .ans--on { background: var(--wash); color: var(--tx); box-shadow: inset 0 0 0 1px var(--acc); }

  /* ── costs ────────────────────────────────────────────────────────────── */
  .cost {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 2px var(--sp-3);
    min-height: var(--row-h);
    align-content: center;
    padding: var(--sp-2) 0;
    border-bottom: 1px solid var(--line);
  }
  .cost__k { font-size: var(--fs-body); font-weight: 600; color: var(--tx); }
  .cost__v { font-family: var(--font-figure); font-size: var(--fs-lead); font-weight: 700; text-align: right; }
  .cost__w { grid-column: 1 / -1; font-size: var(--fs-micro); color: var(--mut); }

  .editor { margin-top: var(--sp-4); padding-top: var(--sp-3); border-top: 1px solid var(--line); }
  .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--sp-3); }
  .f { display: flex; flex-direction: column; gap: 4px; font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .f input {
    min-height: 38px;
    padding: 8px 10px;
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--r-control);
  }
  .acts { display: flex; justify-content: flex-end; margin-top: var(--sp-3); }

  /* ── history ──────────────────────────────────────────────────────────── */
  .hist {
    display: flex;
    align-items: flex-start;
    gap: var(--sp-2);
    margin: 0;
    padding: var(--sp-2) 0;
    font-size: var(--fs-small);
    color: var(--tx2);
    border-bottom: 1px solid var(--line);
  }
  .hist:last-child { border-bottom: none; }
  .hist__dot {
    width: 7px;
    height: 7px;
    margin-top: 6px;
    border-radius: 50%;
    background: var(--mut);
    flex-shrink: 0;
  }
  /* Machine entries sit in the same stream, marked by an amber dot and by the
     word "automatically" — never moved to a tab of their own. */
  .hist__dot--machine { background: var(--warn); }
  .hist__mark { color: var(--warn); font-weight: 700; }

  .banner {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: var(--sp-3) 0 0;
    padding: var(--sp-2) var(--sp-3);
    border-left: 2px solid var(--warn);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-small);
    color: var(--tx2);
  }
  .banner--quiet { border-left-color: var(--mut); margin: 0; }

  .err { display: inline-flex; align-items: center; gap: 5px; font-size: var(--fs-small); font-weight: 700; color: var(--warn); }
  .lnk { display: inline-flex; align-items: center; gap: 5px; font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  .num { font-variant-numeric: tabular-nums; }
  .foot { display: flex; align-items: center; gap: var(--sp-3); }

  /* Tables and two-column layouts become one column in a narrow container, never
     a sideways scroll. The threshold is the design system's, not a new one. */
  @container panel (max-width: 760px) {
    .page { grid-template-columns: 1fr; }
    .hero__num { text-align: left; }
  }

  /* ── the print sheet ──────────────────────────────────────────────────────
     One page, black on white — the single exception to the dark theme, and the
     artefact that makes the portal trusted on the day. Shell's chrome is hidden
     through :global because it belongs to another component. */
  @media print {
    :global(.rail), :global(.head.sd-header), :global(.keys) { display: none !important; }
    :global(.shell) { display: block !important; background: #fff !important; }
    :global(.content) { padding: 0 !important; max-width: none !important; }
    :global(body) { background: #fff !important; color: #000 !important; }

    .crumb, .kinds, .editor, .foot, .banner { display: none; }
    .page { grid-template-columns: 1fr 220px; gap: 10px; }
    .card, .hero { border: 1px solid #999; background: #fff; box-shadow: none; padding: 8px 10px; }
    .hero__ground { display: none; }
    .hero__body, .hero__num { padding: 0; }
    .hero__name, .fact__k, .cost__k, .q__ask, .hist { color: #000; }
    .fact__w, .cost__w, .sub, .hero__prov { color: #333; }
    .q__acts { display: none; }
    .card, .q, .fact, .cost { break-inside: avoid; }
  }
</style>
